import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCalendar, FiStar } from 'react-icons/fi';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { useTranslation } from '../hooks/useTranslation';
import { zodiacSigns } from '../data/zodiacSigns';
import './Zodiac.css';

const getCurrentMonthZodiac = () => {
  const now = new Date();
  const month = now.getMonth();
  const day = now.getDate();
  const monthName = now.toLocaleString('en-US', { month: 'long' });
  const year = now.getFullYear();

  const monthSignMap = [
    { first: 'Capricorn', cutDay: 19, second: 'Aquarius' },
    { first: 'Aquarius', cutDay: 18, second: 'Pisces' },
    { first: 'Pisces', cutDay: 20, second: 'Aries' },
    { first: 'Aries', cutDay: 19, second: 'Taurus' },
    { first: 'Taurus', cutDay: 20, second: 'Gemini' },
    { first: 'Gemini', cutDay: 20, second: 'Cancer' },
    { first: 'Cancer', cutDay: 22, second: 'Leo' },
    { first: 'Leo', cutDay: 22, second: 'Virgo' },
    { first: 'Virgo', cutDay: 22, second: 'Libra' },
    { first: 'Libra', cutDay: 22, second: 'Scorpio' },
    { first: 'Scorpio', cutDay: 21, second: 'Sagittarius' },
    { first: 'Sagittarius', cutDay: 21, second: 'Capricorn' },
  ];

  const info = monthSignMap[month];
  const currentSignName = day <= info.cutDay ? info.first : info.second;
  const firstSign = zodiacSigns.find(s => s.name === info.first);
  const secondSign = zodiacSigns.find(s => s.name === info.second);

  return { monthName, year, firstSign, secondSign, cutDay: info.cutDay, currentSignName };
};

const Zodiac = () => {
  const [filterElement, setFilterElement] = useState('all');
  const hydrated = useHydrated();
  const { t } = useTranslation();
  const currentMonth = getCurrentMonthZodiac();

  const elements = ['all', 'Fire', 'Earth', 'Air', 'Water'];

  const filteredSigns = filterElement === 'all'
    ? zodiacSigns
    : zodiacSigns.filter(sign => sign.element === filterElement);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <div className="zodiac-page">
      <SEO
        title="Zodiac Signs - Characteristics, Traits & Compatibility"
        description="Explore all 12 zodiac signs with detailed characteristics, personality traits, compatibility, lucky numbers, and ruling planets. Filter by Fire, Earth, Air, and Water elements."
        keywords="zodiac signs, zodiac characteristics, zodiac compatibility, fire signs, earth signs, air signs, water signs, astrology signs"
        canonicalPath="/zodiac"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "All 12 Zodiac Signs",
          "description": "Explore the unique characteristics, traits, and compatibility of all 12 zodiac signs"
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is the difference between Vedic and Western zodiac signs?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Vedic astrology uses the sidereal zodiac (fixed star positions) while Western astrology uses the tropical zodiac (based on Earth's seasons). Due to Earth's axial precession, the two systems are currently about 23 degrees apart. This means your Vedic Sun sign may be one sign behind your Western Sun sign. Vedic astrology also emphasizes the Moon sign more than the Sun sign for personality analysis."
              }
            },
            {
              "@type": "Question",
              "name": "How do I find my Vedic zodiac sign?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Your Vedic zodiac sign (Rashi) is determined by the position of the Moon in your birth chart, not just the Sun's position as in Western astrology. Use our free Kundli generator by entering your date, time, and place of birth to find your exact Vedic Moon sign, Sun sign, and Ascendant (Lagna)."
              }
            },
            {
              "@type": "Question",
              "name": "Which zodiac sign is the most powerful?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "In Vedic astrology, no single sign is considered universally most powerful. The strength of a sign depends on the planets placed in it and the house it occupies in a person's birth chart. Leo (ruled by the Sun) and Scorpio (co-ruled by Mars and Ketu) are considered intense and transformative. The sign of your Lagna (Ascendant) is particularly significant as it shapes your overall personality and life path."
              }
            },
            {
              "@type": "Question",
              "name": "Can my personality change based on planetary transits?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "While your core personality (determined by your birth chart) remains stable, planetary transits temporarily influence your mood, behavior, and opportunities. Saturn transits can bring discipline and life lessons, Jupiter transits expand opportunities, and Mars transits increase energy and drive. Understanding these cycles helps you navigate life's phases with greater awareness and wisdom."
              }
            }
          ]
        }]}
      />
      <div className="container">
        <motion.div
          className="page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>{t('zodiac.title')} <span className="gradient-text">{t('zodiac.titleHighlight')}</span></h1>
          <p>{t('zodiac.subtitle')}</p>
        </motion.div>

        {/* Current Month Section */}
        <motion.div
          className="current-month-section"
          initial={hydrated ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="current-month-title">
            <FiCalendar /> {currentMonth.monthName} {currentMonth.year} Zodiac Signs
          </h2>
          <p className="current-month-subtitle">
            {currentMonth.firstSign.name} (until {currentMonth.monthName} {currentMonth.cutDay}) &amp; {currentMonth.secondSign.name} (from {currentMonth.monthName} {currentMonth.cutDay + 1})
          </p>
          <div className="current-month-signs">
            {[currentMonth.firstSign, currentMonth.secondSign].map((sign) => {
              const isActive = currentMonth.currentSignName === sign.name;
              return (
                <Link
                  key={sign.id}
                  to={`/zodiac/${sign.name.toLowerCase()}-${sign.tamilSlug}-rasi`}
                  className={`current-sign-card ${isActive ? 'active-sign' : ''}`}
                  style={{ '--sign-color': sign.color }}
                >
                  {isActive && <span className="active-now-badge"><FiStar /> {t('zodiac.activeNow')}</span>}
                  <span className="current-sign-symbol">{sign.symbol}</span>
                  <h3>{sign.name} <span className="tamil-name">({sign.tamilNameEn})</span></h3>
                  <span className="current-sign-dates">{sign.dates}</span>
                  <p className="current-sign-desc">{sign.description.substring(0, 120)}...</p>
                  <div className="current-sign-details">
                    <span><strong>{t('zodiac.element')}:</strong> {sign.element}</span>
                    <span><strong>{t('zodiac.planet')}:</strong> {sign.ruling_planet}</span>
                    <span><strong>{t('zodiac.luckyDay')}:</strong> {sign.lucky_day}</span>
                  </div>
                  <div className="current-sign-traits">
                    {sign.traits.slice(0, 3).map((trait, i) => (
                      <span key={i} className="current-trait">{trait}</span>
                    ))}
                  </div>
                  <span className="explore-link">{t('zodiac.exploreSign')}</span>
                </Link>
              );
            })}
          </div>
        </motion.div>

        {/* Element Filter */}
        <div className="element-filter">
          {elements.map((element) => (
            <button
              key={element}
              className={`filter-btn ${filterElement === element ? 'active' : ''}`}
              onClick={() => setFilterElement(element)}
            >
              {element === 'all' ? t('zodiac.allSigns') : t(`zodiac.${element.toLowerCase()}Signs`)}
            </button>
          ))}
        </div>

        {/* Zodiac Grid */}
        <motion.div
          className="zodiac-grid"
          variants={containerVariants}
          initial={hydrated ? "hidden" : false}
          animate="visible"
          key={filterElement}
        >
          {filteredSigns.map((sign) => (
            <motion.div key={sign.id} variants={itemVariants}>
              <Link
                to={`/zodiac/${sign.name.toLowerCase()}-${sign.tamilSlug}-rasi`}
                className="zodiac-detail-card"
                style={{ '--sign-color': sign.color }}
              >
                <div className="card-glow"></div>
                <div className="card-content">
                  <div className="card-header">
                    <span className="zodiac-symbol">{sign.symbol}</span>
                    <div className="card-info">
                      <h3>{sign.name} <span className="tamil-name">({sign.tamilNameEn})</span></h3>
                      <span className="card-dates">{sign.dates}</span>
                    </div>
                  </div>

                  <div className="card-details">
                    <div className="detail-item">
                      <span className="detail-label">{t('zodiac.element')}</span>
                      <span className="detail-value">{sign.element}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">{t('zodiac.planet')}</span>
                      <span className="detail-value">{sign.ruling_planet}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">{t('zodiac.quality')}</span>
                      <span className="detail-value">{sign.quality}</span>
                    </div>
                  </div>

                  <div className="card-traits">
                    {sign.traits.slice(0, 3).map((trait, index) => (
                      <span key={index} className="trait-tag">{trait}</span>
                    ))}
                  </div>

                  <span className="explore-link">{t('zodiac.exploreSign')}</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Element Info */}
        <div className="element-info-section">
          <h3>{t('zodiac.understandingElements')}</h3>
          <div className="elements-grid">
            <div className="element-card fire">
              <h4>{t('zodiac.fireTitle')}</h4>
              <p>{t('zodiac.fireDesc')}</p>
              <span>{t('zodiac.fireTrait')}</span>
            </div>
            <div className="element-card earth">
              <h4>{t('zodiac.earthTitle')}</h4>
              <p>{t('zodiac.earthDesc')}</p>
              <span>{t('zodiac.earthTrait')}</span>
            </div>
            <div className="element-card air">
              <h4>{t('zodiac.airTitle')}</h4>
              <p>{t('zodiac.airDesc')}</p>
              <span>{t('zodiac.airTrait')}</span>
            </div>
            <div className="element-card water">
              <h4>{t('zodiac.waterTitle')}</h4>
              <p>{t('zodiac.waterDesc')}</p>
              <span>{t('zodiac.waterTrait')}</span>
            </div>
          </div>
        </div>

        {/* Bottom SEO Content */}
        <div className="page-bottom-content">
          <h2>All 12 Zodiac Signs - Characteristics, Compatibility & Traits</h2>
          <p>Explore the complete guide to all 12 zodiac signs in Vedic astrology. Each sign is governed by a ruling planet, belongs to one of four elements (Fire, Earth, Air, Water), and has a unique quality (Cardinal, Fixed, Mutable) that shapes personality traits, strengths, and life tendencies.</p>
          <p>Understanding your zodiac sign helps you discover your core personality, relationship compatibility, career strengths, and life purpose. The Fire signs (Aries, Leo, Sagittarius) are passionate and dynamic. Earth signs (Taurus, Virgo, Capricorn) are practical and grounded. Air signs (Gemini, Libra, Aquarius) are intellectual and communicative. Water signs (Cancer, Scorpio, Pisces) are emotional and intuitive.</p>
          <p>Click on any zodiac sign to explore detailed characteristics, compatible signs, lucky numbers, ruling planets, and personalized daily horoscope predictions based on authentic Vedic astrology.</p>

          <h3>Zodiac Signs and Their Ruling Planets</h3>
          <p>In Vedic astrology, each zodiac sign is ruled by one of nine planets (Navagraha). Aries and Scorpio are ruled by Mars; Taurus and Libra by Venus; Gemini and Virgo by Mercury; Cancer by the Moon; Leo by the Sun; Sagittarius and Pisces by Jupiter; Capricorn and Aquarius by Saturn; Rahu and Ketu (shadow planets) are assigned to Aquarius and Scorpio in some traditions. The ruling planet of your sign significantly influences your personality, life themes, and karmic direction. A strong ruling planet in the birth chart indicates resilience and success in the areas governed by that planet.</p>

          <h3>Understanding Zodiac Sign Qualities</h3>
          <p><strong>Cardinal Signs (Aries, Cancer, Libra, Capricorn):</strong> Cardinal signs initiate action and are natural leaders. They excel at starting new projects and creating change but may struggle with follow-through. These signs mark the beginning of each season and carry dynamic, initiating energy.</p>
          <p><strong>Fixed Signs (Taurus, Leo, Scorpio, Aquarius):</strong> Fixed signs have tremendous willpower and determination. They are loyal, persistent, and excellent at seeing projects to completion. They can be stubborn and resistant to change but provide stability and reliability in relationships and work.</p>
          <p><strong>Mutable Signs (Gemini, Virgo, Sagittarius, Pisces):</strong> Mutable signs are adaptable, flexible, and communicative. They transition between situations gracefully and are excellent at synthesizing information. They can be inconsistent but bring creativity and versatility to everything they do.</p>

          <h3>Zodiac Compatibility in Vedic Astrology</h3>
          <p>In Vedic astrology, compatibility between zodiac signs is analyzed through multiple lenses. Signs in a trine relationship (1-5-9 from each other) share the same element and have natural harmony — for example, Aries, Leo, and Sagittarius (Fire signs) complement each other beautifully. Signs in a 7th position from each other (opposing signs) like Aries-Libra or Cancer-Capricorn can have complementary energies that create attraction and balance. For accurate marriage compatibility, however, a complete Kundli matching using 12 Porutham (South Indian) or Ashtakoota (North Indian) systems is essential, as it analyzes Nakshatra compatibility at a deeper level than just zodiac signs.</p>

          <h3>Frequently Asked Questions About Zodiac Signs</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>What is the difference between Vedic and Western zodiac signs?</h4>
              <p>Vedic astrology uses the sidereal zodiac (fixed star positions) while Western astrology uses the tropical zodiac (based on Earth's seasons). Due to Earth's axial precession, the two systems are currently about 23 degrees apart. This means your Vedic Sun sign may be one sign behind your Western Sun sign. Vedic astrology also emphasizes the Moon sign more than the Sun sign for personality analysis.</p>
            </div>
            <div className="faq-seo-item">
              <h4>How do I find my Vedic zodiac sign?</h4>
              <p>Your Vedic zodiac sign (Rashi) is determined by the position of the Moon in your birth chart, not just the Sun's position as in Western astrology. Use our free Kundli generator by entering your date, time, and place of birth to find your exact Vedic Moon sign, Sun sign, and Ascendant (Lagna).</p>
            </div>
            <div className="faq-seo-item">
              <h4>Which zodiac sign is the most powerful?</h4>
              <p>In Vedic astrology, no single sign is considered universally "most powerful." The strength of a sign depends on the planets placed in it and the house it occupies in a person's birth chart. Leo (ruled by the Sun) and Scorpio (co-ruled by Mars and Ketu) are considered intense and transformative. The sign of your Lagna (Ascendant) is particularly significant as it shapes your overall personality and life path.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Can my personality change based on planetary transits?</h4>
              <p>While your core personality (determined by your birth chart) remains stable, planetary transits temporarily influence your mood, behavior, and opportunities. Saturn transits can bring discipline and life lessons, Jupiter transits expand opportunities, and Mars transits increase energy and drive. Understanding these cycles helps you navigate life's phases with greater awareness and wisdom.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Zodiac;
